import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

import TicketsManager from "../../components/TicketsManagerTabs/";
import Ticket from "../../components/Ticket/";
import { i18n } from "../../translate/i18n";
import { CHAT } from "../../theme/redesign";

const STORAGE_KEY = "watoolx_tickets_list_width_px";

function clampListWidth(px) {
	const min = 200;
	const max = Math.max(min + 80, Math.floor(window.innerWidth * 0.78));
	return Math.min(Math.max(px, min), max);
}

function readInitialWidth() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const n = raw ? parseInt(raw, 10) : NaN;
		if (Number.isFinite(n)) return clampListWidth(n);
	} catch {
		/* noop */
	}
	return clampListWidth(Math.round(window.innerWidth * 0.25));
}

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: 12,
		height: "100%",
		overflowY: "hidden",
		minWidth: 0,
		backgroundColor: theme.palette.fancyBackground,
		[theme.breakpoints.down("sm")]: {
			height: "100%",
			padding: 0,
		},
	},

	chatPapper: {
		display: "flex",
		height: "100%",
	},

	splitRoot: {
		display: "flex",
		flexDirection: "row",
		height: "100%",
		width: "100%",
		minHeight: 0,
		overflow: "hidden",
		gap: 12,
		[theme.breakpoints.down("sm")]: {
			gap: 0,
		},
	},

	listPane: {
		flex: "none",
		display: "flex",
		flexDirection: "column",
		height: "100%",
		minWidth: 200,
		overflow: "hidden",
		borderRadius: 16,
		backgroundColor: theme.mode === "dark" ? "#111827" : "#ffffff",
		boxShadow:
			theme.mode === "dark"
				? "0 8px 28px rgba(0,0,0,0.4)"
				: "0 8px 28px rgba(15,23,42,0.08)",
		[theme.breakpoints.down("sm")]: {
			width: "100% !important",
			minWidth: 0,
			flex: 1,
			borderRadius: 0,
			boxShadow: "none",
		},
	},
	listPaneMobileHidden: {
		[theme.breakpoints.down("sm")]: {
			display: "none !important",
		},
	},

	resizer: {
		flex: "none",
		width: 8,
		marginLeft: -2,
		cursor: "col-resize",
		backgroundColor: "transparent",
		position: "relative",
		zIndex: 2,
		touchAction: "none",
		"&::after": {
			content: '""',
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: 3,
			height: 44,
			borderRadius: 999,
			backgroundColor:
				theme.palette.type === "dark" || theme.mode === "dark" ? "#4b5563" : "#cbd5e1",
		},
		"&:hover, &:focus": {
			backgroundColor:
				theme.mode === "dark"
					? "rgba(59,130,246,0.18)"
					: "rgba(29,78,216,0.08)",
			outline: "none",
			"&::after": {
				backgroundColor: theme.palette.primary.main,
				height: 64,
			},
		},
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},

	messagesWrapper: {
		flex: 1,
		display: "flex",
		height: "100%",
		flexDirection: "column",
		minWidth: 240,
		overflow: "hidden",
		borderRadius: 16,
		backgroundColor: theme.mode === "dark" ? "#0b1220" : "#efeae2",
		boxShadow:
			theme.mode === "dark"
				? "0 8px 28px rgba(0,0,0,0.4)"
				: "0 8px 28px rgba(15,23,42,0.08)",
		[theme.breakpoints.down("sm")]: {
			minWidth: 0,
			width: "100%",
			borderRadius: 0,
			boxShadow: "none",
		},
	},
	messagesWrapperMobileHidden: {
		[theme.breakpoints.down("sm")]: {
			display: "none !important",
		},
	},
	welcomeMsg: {
		backgroundColor: CHAT.paneBg(theme),
		backgroundImage: CHAT.panePattern(theme),
		backgroundRepeat: "repeat",
		backgroundSize: "340px 340px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		padding: theme.spacing(3),
		border: "none",
	},
	welcomeCard: {
		maxWidth: 420,
		padding: theme.spacing(4, 3),
		borderRadius: 20,
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.mode === "dark" ? "#263248" : "#e5e8f0"}`,
		boxShadow:
			theme.mode === "dark"
				? "0 14px 30px -10px rgba(0,0,0,0.6)"
				: "0 10px 26px -8px rgba(20,28,48,0.14)",
	},
	logoContainer: {
		marginBottom: theme.spacing(2),
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	logoImage: {
		maxWidth: "180px",
		maxHeight: "72px",
		width: "auto",
		height: "auto",
		objectFit: "contain",
		imageRendering: "auto",
	},
	welcomeText: {
		marginBottom: theme.spacing(1),
		textAlign: "center",
		fontSize: "1.25rem",
		fontWeight: 700,
		letterSpacing: "-0.02em",
	},
	subtitleText: {
		marginBottom: theme.spacing(2),
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: "1rem",
	},
	actionButton: {
		marginTop: theme.spacing(2),
	},
}));

const logo = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logotipos/login.png`;
const randomValue = Math.random();

const logoWithRandom = `${logo}?r=${randomValue}`;

const TicketsCustom = () => {
	const classes = useStyles();
	const { ticketId } = useParams();
	const [listWidthPx, setListWidthPx] = useState(readInitialWidth);

	useEffect(() => {
		const onResize = () => setListWidthPx((w) => clampListWidth(w));
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const beginResize = useCallback(
		(startClientX) => {
			const startW = listWidthPx;
			let lastW = startW;

			const onMove = (ev) => {
				const x = ev.clientX != null ? ev.clientX : ev.touches && ev.touches[0] && ev.touches[0].clientX;
				if (x == null) return;
				if (ev.cancelable && ev.type === "touchmove") ev.preventDefault();
				lastW = clampListWidth(startW + (x - startClientX));
				setListWidthPx(lastW);
			};

			const onEnd = () => {
				document.removeEventListener("mousemove", onMove);
				document.removeEventListener("mouseup", onEnd);
				document.removeEventListener("touchmove", onMove);
				document.removeEventListener("touchend", onEnd);
				document.removeEventListener("touchcancel", onEnd);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
				try {
					localStorage.setItem(STORAGE_KEY, String(lastW));
				} catch {
					/* noop */
				}
			};

			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
			document.addEventListener("mousemove", onMove);
			document.addEventListener("mouseup", onEnd);
			document.addEventListener("touchmove", onMove, { passive: false });
			document.addEventListener("touchend", onEnd);
			document.addEventListener("touchcancel", onEnd);
		},
		[listWidthPx]
	);

	return (
		<div className={classes.chatContainer}>
			<div className={classes.chatPapper}>
				<div className={classes.splitRoot}>
					<div
						className={clsx(classes.listPane, ticketId && classes.listPaneMobileHidden)}
						style={{ width: listWidthPx }}
					>
						<TicketsManager />
					</div>
					<div
						className={classes.resizer}
						role="separator"
						aria-orientation="vertical"
						aria-label={i18n.t("tickets.aria.resizeTicketsPanel")}
						tabIndex={0}
						onMouseDown={(e) => {
							e.preventDefault();
							beginResize(e.clientX);
						}}
						onTouchStart={(e) => {
							if (e.touches.length !== 1) return;
							e.preventDefault();
							beginResize(e.touches[0].clientX);
						}}
						onKeyDown={(e) => {
							const step = e.shiftKey ? 24 : 8;
							if (e.key === "ArrowLeft") {
								e.preventDefault();
								setListWidthPx((w) => {
									const n = clampListWidth(w - step);
									try {
										localStorage.setItem(STORAGE_KEY, String(n));
									} catch {
										/* noop */
									}
									return n;
								});
							}
							if (e.key === "ArrowRight") {
								e.preventDefault();
								setListWidthPx((w) => {
									const n = clampListWidth(w + step);
									try {
										localStorage.setItem(STORAGE_KEY, String(n));
									} catch {
										/* noop */
									}
									return n;
								});
							}
						}}
					/>
					<div className={clsx(classes.messagesWrapper, !ticketId && classes.messagesWrapperMobileHidden)}>
						{ticketId ? (
							<Ticket />
						) : (
							<Paper square variant="outlined" className={classes.welcomeMsg}>
								<div className={classes.welcomeCard}>
								<div className={classes.logoContainer}>
									<img
										className={classes.logoImage}
										src={logoWithRandom}
										alt={`${
											typeof process !== "undefined" && process.env
												? process.env.REACT_APP_NAME_SYSTEM
												: "Sistema"
										}`}
									/>
								</div>

								<Typography
									variant="h6"
									color="textPrimary"
									className={classes.welcomeText}
								>
									{i18n.t("tickets.welcome.title")}
								</Typography>

								<Typography variant="body2" color="textSecondary" className={classes.subtitleText}>
									{i18n.t("tickets.welcome.subtitle")}
								</Typography>
								</div>
							</Paper>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TicketsCustom;
